import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import ScrollToTop from "@/components/base/ScrollToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import Layout from "@/components/feature/Layout";
import { getArticleById } from "@/mocks/articles";

const articleImages: Record<number, string> = {
  1: "https://readdy.ai/api/search-image?query=Energetic%20modern%20gaming%20beginner%20guide%20concept%20warm%20orange%20amber%20accents%20glowing%20tutorial%20cards%20abstract%20geometric%20digital%20interface%20friendly%20atmosphere%20editorial&width=1200&height=600&seq=article-detail-01&orientation=landscape",
  2: "https://readdy.ai/api/search-image?query=Dynamic%20competition%20calendar%20concept%20with%20golden%20trophies%20warm%20amber%20gradient%20energetic%20esports%20atmosphere%20glowing%20date%20markers%20professional%20editorial&width=1200&height=600&seq=article-detail-02&orientation=landscape",
  3: "https://readdy.ai/api/search-image?query=Clean%20modern%20ranking%20system%20dashboard%20visualization%20warm%20orange%20accent%20colors%20abstract%20data%20charts%20glowing%20leaderboard%20lines%20editorial%20style&width=1200&height=600&seq=article-detail-03&orientation=landscape",
  4: "https://readdy.ai/api/search-image?query=Team%20collaboration%20and%20strategy%20concept%20warm%20amber%20tones%20silhouettes%20of%20players%20united%20abstract%20geometric%20network%20connections%20energetic%20atmosphere&width=1200&height=600&seq=article-detail-04&orientation=landscape",
  5: "https://readdy.ai/api/search-image?query=Relaxed%20casual%20mobile%20gaming%20lifestyle%20concept%20warm%20orange%20accents%20soft%20cozy%20atmosphere%20smartphone%20with%20glowing%20game%20interface%20editorial%20style&width=1200&height=600&seq=article-detail-05&orientation=landscape",
  6: "https://readdy.ai/api/search-image?query=Knowledge%20quiz%20brain%20challenge%20concept%20warm%20amber%20glow%20abstract%20question%20marks%20floating%20light%20bulb%20geometric%20patterns%20smart%20atmosphere%20editorial&width=1200&height=600&seq=article-detail-06&orientation=landscape",
  7: "https://readdy.ai/api/search-image?query=Reward%20and%20prize%20showcase%20concept%20warm%20golden%20glow%20gift%20boxes%20abstract%20luxury%20presentation%20geometric%20shapes%20elegant%20atmosphere%20editorial&width=1200&height=600&seq=article-detail-07&orientation=landscape",
  8: "https://readdy.ai/api/search-image?query=Professional%20esports%20arena%20elite%20competition%20concept%20warm%20orange%20dramatic%20lighting%20silhouette%20of%20champion%20trophy%20epic%20scale%20cinematic%20atmosphere&width=1200&height=600&seq=article-detail-08&orientation=landscape",
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = id ? parseInt(id, 10) : 0;
  const article = getArticleById(articleId);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 17c每日大赛`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute("content", article.excerpt);
      }
    }

    const existingScript = document.getElementById("article-schema");
    if (existingScript) existingScript.remove();

    if (article) {
      const schema = {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": article.title,
        "description": article.excerpt,
        "author": { "@type": "Person", "name": article.author },
        "datePublished": article.date,
        "publisher": {
          "@type": "Organization",
          "name": "17c每日大赛",
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": window.location.href,
        },
      };

      const script = document.createElement("script");
      script.id = "article-schema";
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    }

    return () => {
      const el = document.getElementById("article-schema");
      if (el) el.remove();
    };
  }, [article]);

  if (!article) {
    return (
      <Layout>
        <div className="pt-24 pb-16 min-h-screen text-center">
          <h2 className="text-xl font-bold text-foreground-950">文章未找到</h2>
          <Link to="/" className="text-primary-600 mt-4 inline-block">返回首页</Link>
        </div>
      </Layout>
    );
  }

  const breadcrumbItems = [
    { label: article.category, href: `/category/${article.categorySlug}` },
    { label: article.title },
  ];

  return (
    <>
      <ScrollToTop />
      <Layout>
        <article className="pt-24 pb-16 bg-background-50 min-h-screen">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <div className="mb-8">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold">
                  {article.category}
                </span>
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span><i className="ri-user-line mr-1"></i>{article.author}</span>
                <span><i className="ri-calendar-line mr-1"></i>{article.date}</span>
                <span><i className="ri-time-line mr-1"></i>{article.readTime}阅读</span>
              </div>
            </div>

            <div className="mb-10 rounded-2xl overflow-hidden">
              <img
                src={articleImages[article.id] || articleImages[1]}
                alt={article.title}
                className="w-full h-auto max-h-[500px] object-cover"
              />
            </div>

            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-4 py-1.5 rounded-full bg-background-100 text-foreground-600 text-sm">
                      #{tag}
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-foreground-500">分享：</span>
                  <a href="#" className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-accent-100 hover:text-accent-600 transition-colors" aria-label="分享到微信">
                    <i className="ri-wechat-line"></i>
                  </a>
                  <a href="#" className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-accent-100 hover:text-accent-600 transition-colors" aria-label="分享到微博">
                    <i className="ri-weibo-line"></i>
                  </a>
                  <a href="#" className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-accent-100 hover:text-accent-600 transition-colors" aria-label="复制链接">
                    <i className="ri-link"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </article>

        <style>{`
          .prose-custom h2 {
            font-size: 1.5rem;
            font-weight: 700;
            margin-top: 2rem;
            margin-bottom: 1rem;
            color: oklch(var(--foreground-950));
            font-family: var(--font-heading);
          }
          .prose-custom h3 {
            font-size: 1.2rem;
            font-weight: 600;
            margin-top: 1.5rem;
            margin-bottom: 0.75rem;
            color: oklch(var(--foreground-900));
          }
          .prose-custom p {
            margin-bottom: 1rem;
            line-height: 1.8;
            color: oklch(var(--foreground-700));
          }
          .prose-custom ul, .prose-custom ol {
            margin-bottom: 1rem;
            padding-left: 1.5rem;
          }
          .prose-custom li {
            margin-bottom: 0.5rem;
            color: oklch(var(--foreground-700));
          }
          .prose-custom strong {
            color: oklch(var(--foreground-950));
            font-weight: 600;
          }
          .prose-custom a {
            color: oklch(var(--primary-500));
            text-decoration: underline;
          }
        `}</style>
      </Layout>
    </>
  );
}